import { propOr } from "ramda";
import React, { Fragment, useEffect } from "react";
import PropTypes from "prop-types";

import Pager from "~/components/Pager";
import Pagination from "~/components/Pagination";
import Viewer from "~/components/Viewer";
import ZoomController from "~/components/ZoomController";
import MaterialOutlineOverlay from "~/components/MaterialOutlineOverlay";

import usePaging from "~/hooks/usePaging";

import EventBus from "~/libs/eventBus";

import { pdfToCssUnits } from "~/utils";
import { getToFormatter, getFromFormatter } from "~/utils/page";
import { selectShowCutout, selectShowModalCutout } from "~/store/ducks/user/uistate";
import { useSelector } from "react-redux";

function EmbeddedViewer({
  channel,
  material,
  getThumbnailPath,
  getImagePath,
  getQuickDialActions,
  currentPage,
  setCurrentPage,
  activeTab
}) {
  const showCutout = useSelector(selectShowCutout);
  const { size, pagesCount } = material;
  const pageOffset = propOr(0, "pageOffset", material);

  useEffect(() => {
    EventBus.dispatch("zoomToFit", "embedded", {
      direction: "top",
      padding: { top: 48 }
    });
  }, []);

  const isDoublePage = false;

  const pageWidth = pdfToCssUnits(size.width);
  const pageHeight = pdfToCssUnits(size.height);
  const totalPageWidth = pageWidth * (isDoublePage ? 2 : 1);
  const totalPageHeight = pageHeight;

  const preOffsetNumbering = propOr("roman", "preOffsetNumbering", material);
  const toFormat = getToFormatter(preOffsetNumbering);
  const fromFormat = getFromFormatter(preOffsetNumbering);

  const { absolutePage, readablePage, goToPrevious, goToNext, gotToReadablePage } = usePaging(
    1,
    pagesCount,
    pageOffset,
    onPageChange,
    toFormat,
    fromFormat,
    currentPage
  );

  const isLeftPagerVisible = absolutePage !== 1;
  const isRightPagerVisible = absolutePage !== pagesCount;

  function onPageChange(page) {
    material.categories.forEach(cat => {
      setCurrentPage({
        categoryTitle: cat.title,
        currentPage: page,
        activeTab
      });
    });
    EventBus.dispatch("zoomToFit", "modal", {
      direction: "top",
      padding: { top: 48 }
    });
  }

  function renderOutlineList() {
    if (material && material.inhaltsverzeichnis && material.inhaltsverzeichnis.length > 0) {
      return (
        <MaterialOutlineOverlay
          outline={material.inhaltsverzeichnis}
          goToPage={gotToReadablePage}
          page={readablePage}
        />
      );
    }
    return null;
  }

  function renderPagination() {
    return (
      <>
        <Pagination
          page={readablePage}
          goToPrevious={goToPrevious}
          goToNext={goToNext}
          goToDestination={gotToReadablePage}
          showGoToPrevious={isLeftPagerVisible}
          showGoToNext={isRightPagerVisible}
        />

        {isLeftPagerVisible && <Pager direction="left" onClick={goToPrevious} />}

        {isRightPagerVisible && <Pager direction="right" onClick={goToNext} />}
      </>
    );
  }

  function renderViewer(zoomController) {
    return (
      <Viewer
        name="embedded"
        bookId={material._id}
        renderPagination={renderPagination}
        renderOutlineList={renderOutlineList}
        zoomController={zoomController}
        isDoublePage={isDoublePage}
        page={absolutePage}
        pages={[absolutePage]}
        pageWidth={pageWidth}
        pageHeight={pageHeight}
        totalPageWidth={totalPageWidth}
        totalPageHeight={totalPageHeight}
        getThumbnailPath={getThumbnailPath}
        getImagePath={getImagePath}
        showAnnotations
        showCutout={showCutout}
        isModalCutout
        showMaterials={false}
        quickDialActions={getQuickDialActions(zoomController, absolutePage)}
      />
    );
  }

  return <ZoomController channel={channel}>{renderViewer}</ZoomController>;
}

EmbeddedViewer.propTypes = {
  channel: PropTypes.string,
  material: PropTypes.object,
  getThumbnailPath: PropTypes.func.isRequired,
  getImagePath: PropTypes.func.isRequired,
  getQuickDialActions: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  activeTab: PropTypes.string.isRequired
};

EmbeddedViewer.defaultProps = {
  quickDialActions: []
};

export default EmbeddedViewer;
